import React from 'react';
import styled, { css } from 'styled-components';
import { rem } from 'polished';
import { space, layout, variant, fontSize } from 'styled-system';
import Spinner from '../Spinner/Spinner';
import Box from '../Box/Box';
import { theme } from '../../../config/theme';
import { track } from '../../utils/analytics';

interface StyledButtonInterface {
    variant?:
        | 'primary'
        | 'secondary'
        | 'white-secondary'
        | 'tertiary'
        | 'white-tertiary'
        | 'ghost-blue'
        | 'ghost-gray'
        | 'ghost-active'
        | 'accent-green'
        | 'accent-litblue';
    colorScheme?: 'white';
    loading?: boolean;
    fullWidth?: boolean;
    size?: 'sm' | 'md' | 'lg';
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
}

export const ghostGrayButtonHoverStyles = {
    bg: theme.colors.primary[50],
    color: theme.colors.primary[1800],
};
export const ghostBlueButtonHoverStyles = {
    bg: theme.colors.primary[50],
    color: theme.colors.primary.main,
};
export const whiteTertiaryButtonHoverStyles = {
    bg: theme.colors.primary[1500],
};

const ContentWrapper = styled.span`
    text-align: center;
    transition: opacity 200ms ease;
`;

const StyledButton = styled(Box)<StyledButtonInterface>(
    {
        position: 'relative',
        display: 'flex',
        gap: rem('8px'),
        justifyContent: 'center',
        alignItems: 'center',
        lineHeight: 1,
        fontWeight: ({ fontWeight }) => (fontWeight ? fontWeight : 'bold'),
        cursor: 'pointer',
        transition: 'all 0.2s ease-out',
        textAlign: 'center',
        width: ({ fullWidth }) => (fullWidth ? '100%' : 'auto'),
        border: 'solid 2px transparent',
        borderRadius: ({ theme }) => theme.radii.sm,
        '&:disabled': {
            cursor: ({ loading }) => (loading ? 'wait' : 'not-allowed'),
        },
        [ContentWrapper]: {
            opacity: ({ loading }) => (loading ? 0 : 1),
        },
        whiteSpace: 'nowrap',
    },
    variant({
        prop: 'variant',
        variants: {
            primary: {
                color: (theme) => theme.colors.white,
                bg: (theme) => theme.colors.primary.main,
                '&:focus, &:hover': {
                    bg: (theme) => theme.colors.primary[1200],
                },
                '&:active': {
                    bg: (theme) => theme.colors.primary[1400],
                },
                '&:disabled': {
                    color: (theme) => theme.colors.neutral[1100],
                    bg: (theme) => theme.colors.neutral[200],
                },
            },
            secondary: {
                color: (theme) => theme.colors.primary[1700],
                bg: 'transparent',
                borderColor: 'currentColor',
                '&:focus, &:hover': {
                    color: (theme) => theme.colors.primary[1200],
                },
                '&:active': {
                    color: (theme) => theme.colors.primary[1400],
                },
                '&:disabled': {
                    color: (theme) => theme.colors.neutral[1100],
                    bg: (theme) => theme.colors.neutral[200],
                    borderColor: 'transparent',
                },
            },
            'white-secondary': {
                color: 'white',
                bg: 'transparent',
                borderColor: 'currentColor',
                '&:focus, &:hover': {
                    color: (theme) => theme.colors.primary[200],
                },
                '&:active': {
                    color: (theme) => theme.colors.primary[400],
                },
                '&:disabled': {
                    color: (theme) => theme.colors.neutral[1100],
                    bg: (theme) => theme.colors.neutral[200],
                    borderColor: 'transparent',
                },
            },
            tertiary: {
                color: theme.colors.primary.main,
                bg: theme.colors.primary[50],
                '&:focus, &:hover': {
                    bg: (theme) => theme.colors.primary[100],
                },
                '&:active': {
                    color: theme.colors.primary[1400],
                    bg: (theme) => theme.colors.primary[200],
                },
                '&:disabled': {
                    color: (theme) => theme.colors.neutral[1100],
                    bg: (theme) => theme.colors.neutral[100],
                    borderColor: 'transparent',
                },
            },
            'white-tertiary': {
                color: theme.colors.neutral.white,
                bg: 'transparent',
                '&:focus, &:hover': whiteTertiaryButtonHoverStyles,
                '&:active': {
                    color: theme.colors.neutral.white,
                    bg: theme.colors.primary[1400],
                },
                '&:disabled': {
                    color: (theme) => theme.colors.neutral[1100],
                    bg: (theme) => theme.colors.neutral[100],
                    borderColor: 'transparent',
                },
            },
            'ghost-blue': {
                color: theme.colors.primary.main,
                '&:focus, &:hover': ghostBlueButtonHoverStyles,
                '&:active': {
                    color: theme.colors.primary[1400],
                    bg: (theme) => theme.colors.primary[200],
                },
                '&:disabled': {
                    color: (theme) => theme.colors.neutral[1100],
                    bg: (theme) => theme.colors.neutral[100],
                    borderColor: 'transparent',
                },
            },
            'ghost-gray': {
                color: theme.colors.neutral['1200'],
                '&:focus, &:hover': ghostGrayButtonHoverStyles,
                '&:active': {
                    color: theme.colors.primary[1800],
                    bg: (theme) => theme.colors.primary[100],
                },
                '&:disabled': {
                    color: (theme) => theme.colors.neutral[1100],
                    bg: (theme) => theme.colors.neutral[100],
                    borderColor: 'transparent',
                },
            },
            'ghost-active': ghostGrayButtonHoverStyles,
            'accent-green': {
                color: (theme) => theme.colors.neutral.black,
                bg: (theme) => theme.colors.secondary[800],
                '&:focus, &:hover': {
                    bg: (theme) => theme.colors.secondary[1000],
                },
                '&:active': {
                    bg: (theme) => theme.colors.secondary[1100],
                },
                '&:disabled': {
                    color: (theme) => theme.colors.neutral[1200],
                    bg: (theme) => theme.colors.neutral[200],
                },
            },
            'accent-litblue': {
                color: (theme) => theme.colors.neutral.black,
                bg: (theme) => theme.colors.tertiary.litblue[900],
                '&:focus, &:hover': {
                    bg: (theme) => theme.colors.tertiary.litblue[1000],
                },
                '&:active': {
                    bg: (theme) => theme.colors.tertiary.litblue[1100],
                },
                '&:disabled': {
                    color: (theme) => theme.colors.neutral[1200],
                    bg: (theme) => theme.colors.neutral[200],
                },
            },
        },
    }),
    variant({
        prop: 'size',
        variants: {
            sm: {
                height: rem('26px'),
                fontSize: rem('12px'),
                paddingInline: rem('8px'),
            },
            md: {
                height: rem('38px'),
                fontSize: rem('14px'),
                paddingInline: rem('12px'),
            },
            lg: {
                height: rem('48px'),
                fontSize: rem('16px'),
                paddingInline: rem('16px'),
            },
        },
    })
);

interface ButtonInterface extends StyledButtonInterface {
    type?: 'button' | 'submit' | 'reset';
    as?: keyof JSX.IntrinsicElements | React.ComponentType<any>;
    disabled?: boolean;
    isLight?: boolean;
    trackingEvent?: string;
}

const DarkModeVariants = {
    primary: 'primary',
    secondary: 'white-secondary',
    tertiary: 'white-tertiary',
};

const Button: React.FC<ButtonInterface> = React.forwardRef(
    (
        {
            children,
            type,
            as = 'button',
            disabled,
            loading = false,
            variant = 'primary',
            size = 'md',
            startIcon,
            endIcon,
            isLight = false,
            onClick = () => {},
            trackingEvent,
            ...props
        },
        ref
    ) => {
        return (
            <StyledButton
                ref={ref}
                type={type}
                as={as}
                disabled={disabled || loading}
                loading={loading ? true : undefined}
                variant={isLight ? DarkModeVariants[variant] : variant}
                size={size}
                onClick={() => {
                    trackingEvent && track(trackingEvent);
                    onClick?.();
                }}
                {...props}
            >
                {loading ? (
                    <Spinner />
                ) : (
                    <>
                        {startIcon}
                        <ContentWrapper>{children}</ContentWrapper>
                        {endIcon}
                    </>
                )}
            </StyledButton>
        );
    }
);

Button.displayName = 'Button';

export default Button;
